/* eslint-disable max-lines */
import { useParams } from "react-router-dom";
import { useAppState } from "@/modules/app/useAppContext";
import { useDateFromSearchParams } from "@/helpers/useTimeHooks";
import { useQuery } from "@tanstack/react-query";
import {
  getSnowflakeJobRuns,
  getSnowflakeJobRunsGraphData,
  getSnowflakeJobsSummary,
  getDbtJobFilters,
} from "@/helpers/apis";
import {
  getDateByEndOfDayWithoutTimeoffset,
  getDateByStartOfDayWithoutTimeoffset,
} from "@/components/dateRange/utils";
import { RelativeComponentLoader } from "@/components/loader";
import { Stack } from "@altimateai/lego";
import { formatBytes, formatCost, formatMilliseconds } from "@/helpers/utils";
import { Alert, AlertDescription, AlertTitle } from "@altimateai/lego";
import { DataTable } from "@/components/tables";
import { GetSnowflakeJobRunItem } from "@/pages/query/types";
import dayjs from "dayjs";
import { Graph } from "@/pages/query/Graph";
import { ExecutionStatusCount } from "@/pages/query/ExecutionStatusCount";
import { GET_DBT_JOB_RUNS, GET_DBT_JOB_SUMMARY } from "@/pages/query/constants";
import usePaginate from "@/components/paginate/usePaginate";
import { Paginate } from "@/components/paginate";
import { useState, useEffect } from "react";
import { FilterType } from "@/components/tables/types";
import { FilterApplicableFor } from "@/components/tags/types";
import {
  useWorkloadRunListParams,
  RUN_STATUS_OPTIONS,
} from "@/pages/query/hooks/useWorkloadRunListParams";
import useFiltersBySearchParams from "@/helpers/useFiltersBySearchParams";
import { DbtCommandHighlight } from "./DbtCommandHighlight";
import { Tooltip } from "@altimateai/lego";

export const DbtJobRuns = () => {
  const { workflowId } = useParams();
  const { currency, enableDbtJobsListing } = useAppState();
  const { startDate, endDate } = useDateFromSearchParams();
  const { page, setPage, pageSize, updatePageSize } = usePaginate();
  const [totalRuns, setTotalRuns] = useState<number>(0);
  const [totalPages, setTotalPages] = useState<number>(0);

  const { sortKey, sortOrder, runStatusFilter, handleRunStatusChange } =
    useWorkloadRunListParams(setPage);
  const { searchParams } = useFiltersBySearchParams();
  const commandFilter = searchParams.getAll("command") || [];
  const encodedWorkflowId = workflowId ? encodeURIComponent(workflowId) : "";

  const {
    data: runs,
    isLoading: isRunsLoading,
    error: runsError,
    isError: isRunsError,
  } = useQuery({
    queryKey: [
      GET_DBT_JOB_RUNS,
      workflowId,
      startDate,
      endDate,
      page,
      pageSize,
      sortKey,
      sortOrder,
      runStatusFilter,
      commandFilter,
    ],
    queryFn: () =>
      getSnowflakeJobRuns(encodedWorkflowId, {
        start_date: getDateByStartOfDayWithoutTimeoffset(startDate).toISOString(),
        end_date: getDateByEndOfDayWithoutTimeoffset(endDate)?.toISOString(),
        page: page + 1,
        size: pageSize,
        sort_key: sortKey,
        sort_order: sortOrder,
        run_status: runStatusFilter,
        command: commandFilter,
      }),
    enabled: Boolean(workflowId),
  });

  // Handle successful data fetch (replaces onSuccess callback removed in TanStack Query v5)
  useEffect(() => {
    if (runs) {
      setTotalRuns(runs?.total ?? 0);
      setTotalPages(runs?.pages ?? 0);
    }
  }, [runs]);

  const { data: graphData, isLoading: isGraphDataLoading } = useQuery({
    queryKey: [GET_DBT_JOB_RUNS, workflowId, startDate, endDate],
    queryFn: () =>
      getSnowflakeJobRunsGraphData(encodeURIComponent(workflowId!), {
        start_date: getDateByStartOfDayWithoutTimeoffset(startDate).toISOString(),
        end_date: getDateByEndOfDayWithoutTimeoffset(endDate)?.toISOString(),
      }),
    enabled: Boolean(workflowId),
  });

  const { data: summaryData, isLoading: isSummaryLoading } = useQuery({
    queryKey: [GET_DBT_JOB_SUMMARY, workflowId, startDate, endDate],
    queryFn: () =>
      getSnowflakeJobsSummary(encodeURIComponent(workflowId!), {
        start_date: getDateByStartOfDayWithoutTimeoffset(startDate).toISOString(),
        end_date: getDateByEndOfDayWithoutTimeoffset(endDate)?.toISOString(),
      }),
  });

  return (
    <div role="region" aria-label="dbt job runs region">
      {isSummaryLoading ? (
        <RelativeComponentLoader label={"Loading Runs"} />
      ) : (
        <>
          <Stack className="al-items-center flex-wrap col-gap-xl" tag="dl">
            {[
              {
                value: formatMilliseconds(summaryData!.execution_time / summaryData!.runs),
                label: "Average runtime",
              },
              {
                value: formatMilliseconds(summaryData!.percentile_90_runtime),
                label: "90th percentile runtime",
              },
              {
                value: formatMilliseconds(summaryData!.max_runtime),
                label: "Max runtime",
              },
              {
                value: formatMilliseconds(summaryData!.min_runtime),
                label: "Min runtime",
              },
              {
                value: summaryData!.runs,
                label: "Total runs",
              },
              {
                value: (summaryData!.success_runs / summaryData!.runs).toFixed(2),
                label: "Success ratio",
              },
              {
                value: formatCost(summaryData!.query_cost / summaryData!.runs, currency),
                label: "Average cost",
              },
            ].map((item, index) => {
              const termId = `summary-metric-${index}-label`;
              return (
                <Stack key={item.label} className="al-items-center gap-xs">
                  <dt id={termId} className="al-text-light-gray al-m-0">
                    {item.label}:
                  </dt>
                  <dd aria-labelledby={termId} className="fw-semibold al-m-0">
                    {item.value}
                  </dd>
                </Stack>
              );
            })}
          </Stack>
          <div className={"al-mt-2"}>
            <Graph isLoading={isGraphDataLoading} data={graphData?.toReversed() ?? []} />
          </div>
          <Stack justify="end" className="al-py-3">
            <div id="dbt-tab-filter" />
          </Stack>
          <Stack direction={"column"} gap={"md"}>
            {isRunsError ? (
              <Alert variant="destructive" role="alert">
                <AlertTitle>Error loading runs</AlertTitle>
                <AlertDescription>
                  {runsError instanceof Error
                    ? runsError.message
                    : "Failed to load runs. Please try again."}
                </AlertDescription>
              </Alert>
            ) : (
              <>
                <DataTable
                  isLoading={isRunsLoading}
                  getRowUrl={item =>
                    `/query/dbt-jobs/${encodeURIComponent(workflowId!)}/models?startDate=${startDate.toISOString()}&endDate=${endDate.toISOString()}&runId=${item.run_id}`
                  }
                  getRowState={item => ({ entityName: item.run_id })}
                  resetPage={() => setPage(0)}
                  data={{
                    items: runs?.items ?? [],
                  }}
                  defaultSort={{ sortParamValue: "start_time", order: "desc" }}
                  applicableFor={FilterApplicableFor.DBT_JOB_RUNS}
                  {...(enableDbtJobsListing ? { portals: { filter: "dbt-tab-filter" } } : {})}
                  header={[
                    ...(enableDbtJobsListing
                      ? [
                          {
                            id: 1,
                            label: "Job",
                            uid: "command_display",
                            width: "20%" as const,
                            filter: {
                              filterType: FilterType.API_DROPDOWN as FilterType.API_DROPDOWN,
                              searchKey: "command",
                              label: "Job",
                              apiFunction: (params: {
                                prefix?: string;
                                page?: number;
                                size?: number;
                              }) =>
                                getDbtJobFilters(encodedWorkflowId, {
                                  page: params.page ?? 1,
                                  size: params.size ?? 50,
                                  prefix: params.prefix,
                                }),
                              extractorFunction: (data: unknown) =>
                                (
                                  (data as { items: { command_display: string }[] }).items ?? []
                                ).map(item => ({
                                  label: item.command_display,
                                  value: item.command_display,
                                  showTooltip: true,
                                })),
                              seeMore: true,
                              pageSize: 50,
                            },
                            render: (item: GetSnowflakeJobRunItem) =>
                              item.command_display ? (
                                <Tooltip
                                  title={
                                    <div className="al-max-w-lg al-break-words al-whitespace-pre-wrap">
                                      <DbtCommandHighlight command={item.command_display} />
                                    </div>
                                  }
                                >
                                  <div className="al-truncate al-overflow-hidden">
                                    <DbtCommandHighlight command={item.command_display} />
                                  </div>
                                </Tooltip>
                              ) : (
                                <span>-</span>
                              ),
                          },
                        ]
                      : []),
                    {
                      id: 2,
                      label: "Run ID",
                      uid: "run_id",
                      width: "15%",
                      render: (item: GetSnowflakeJobRunItem) => item.run_id,
                    },
                    {
                      id: 3,
                      label: "Run Cost",
                      uid: "query_cost",
                      sort: {
                        sortSearchKey: "sort_key",
                        sortOrderSearchKey: "sort_order",
                        sortParamValue: "run_cost",
                      },
                      render: (item: GetSnowflakeJobRunItem) => (
                        <div>{formatCost(item.query_cost, currency)}</div>
                      ),
                    },
                    {
                      id: 4,
                      label: "Bytes Scanned",
                      uid: "bytes_scanned",
                      sort: {
                        sortSearchKey: "sort_key",
                        sortOrderSearchKey: "sort_order",
                      },
                      render: (item: GetSnowflakeJobRunItem) => (
                        <div>{formatBytes(item.bytes_scanned)}</div>
                      ),
                    },
                    {
                      id: 5,
                      label: "Start Time",
                      uid: "start_time",
                      sort: {
                        sortSearchKey: "sort_key",
                        sortOrderSearchKey: "sort_order",
                      },
                      render: (item: GetSnowflakeJobRunItem) => (
                        <div>{dayjs(item.start_time).format("YYYY-MM-DD hh:mm a")}</div>
                      ),
                    },
                    {
                      id: 6,
                      label: "Execution Time",
                      uid: "execution_time",
                      sort: {
                        sortSearchKey: "sort_key",
                        sortOrderSearchKey: "sort_order",
                      },
                      render: (item: GetSnowflakeJobRunItem) => (
                        <div>{formatMilliseconds(item.execution_time)}</div>
                      ),
                    },
                    {
                      id: 7,
                      label: "Query Status",
                      uid: "task_status",
                      filter: {
                        filterType: FilterType.DROPDOWN_SINGLE,
                        searchKey: "run_status",
                        options: RUN_STATUS_OPTIONS,
                        label: "Run Status",
                        value: runStatusFilter,
                        onChange: handleRunStatusChange,
                      },
                      render: (item: GetSnowflakeJobRunItem) => (
                        <Tooltip
                          title={`${item.success_count}/${item.total_count} queries succeeded`}
                        >
                          <div>
                            <ExecutionStatusCount
                              run_count={item.total_count}
                              success_count={item.success_count}
                            />
                          </div>
                        </Tooltip>
                      ),
                    },
                  ]}
                />
                <Paginate
                  isLoading={isRunsLoading}
                  itemCount={totalRuns}
                  page={page}
                  pageSize={pageSize}
                  numPages={totalPages}
                  onPageClick={setPage}
                  onPageSizeChange={updatePageSize}
                  showPageSize
                />
              </>
            )}
          </Stack>
        </>
      )}
    </div>
  );
};
